import React, { useState, useEffect } from 'react';
import { Layers, Eye, EyeOff, ChevronRight } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const GARMENT_PREFIX_MAP = {
  trouser: 'T',
  shirt: 'S',
  jacket: 'J',
  waistcoat: 'W',
  overcoat: 'O',
};

const VERSION_COLORS = ['#1e40af', '#dc2626', '#16a34a']; // Blue, Red, Green

export default function RightSidebar({ 
  showFront, 
  setShowFront,
  showSide,
  setShowSide,
  showBack,
  setShowBack,
  showYoke,
  setShowYoke,
  hasYokePiece,
  showCollar,
  setShowCollar,
  hasCollarPiece,
  showSleeve,
  setShowSleeve,
  hasSleevePiece,
  hasSidePiece,
  showMeasurements,
  setShowMeasurements,
  seamWalkMode,
  setSeamWalkMode,
  sleeveOnBodyMode,
  setSleeveOnBodyMode,
  isOpen,
  onToggle,
  currentPattern,
  garmentDisplayOptions,
  selectedVersions,
  setSelectedVersions,
  onVersionsLoaded,
  token,
  autosaveStatus = 'idle',
  autosaveMessage = 'Idle',
  onRetryAutosave = () => {},
}) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && token && currentPattern) {
      fetchVersions();
    }
  }, [isOpen, token, currentPattern]);

  const fetchVersions = async () => {
    if (!currentPattern) return;
    
    setLoading(true);
    try {
      const xhr = new XMLHttpRequest();
      const garmentType = (currentPattern.garment_type || 'trouser').toLowerCase();
      const query = new URLSearchParams({
        client_number: currentPattern.client_number,
        garment_type: garmentType,
      });
      xhr.open('GET', `${API_BASE}/api/patterns?${query.toString()}`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      xhr.onload = () => {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          const normalizedClientNumber = String(currentPattern.client_number || '').toUpperCase();
          const expectedPrefix = GARMENT_PREFIX_MAP[garmentType] || 'T';
          // Filter out current pattern and any cross-garment records
          const filtered = data.filter((pattern) => {
            const isCurrent = pattern.id === currentPattern.id;
            const sameGarment = (pattern.garment_type || 'trouser').toLowerCase() === garmentType;
            const sameClient = String(pattern.client_number || '').toUpperCase() === normalizedClientNumber;
            const sameGarmentPrefix = String(pattern.pattern_number || '').toUpperCase().startsWith(`${expectedPrefix}${normalizedClientNumber}`);
            return !isCurrent && sameGarment && sameClient && sameGarmentPrefix;
          });
          setVersions(filtered);
          if (onVersionsLoaded) {
            onVersionsLoaded(filtered);
          }
        }
      };
      
      xhr.send();
    } catch (err) {
      console.error('Failed to fetch versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionToggle = (versionId) => {
    setSelectedVersions(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      } else if (prev.length < 3) {
        return [...prev, versionId];
      }
      return prev;
    });
  };

  const resolvedDisplayOptions = {
    measurements: true,
    points: true,
    seamWalk: false,
    sleeveOnBody: false,
    ...(garmentDisplayOptions || {}),
  };

  const autosaveToneClass = autosaveStatus === 'saving'
    ? 'border-amber-200 bg-amber-50 text-amber-700'
    : autosaveStatus === 'saved'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : autosaveStatus === 'error'
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : 'border-slate-200 bg-slate-50 text-slate-700';

  // Don't show anything if no pattern is loaded
  if (!currentPattern) {
    return null;
  }

  // Collapsed state
  if (!isOpen) {
    return (
      <div className="w-12 flex-shrink-0 border-l bg-card h-full flex flex-col items-center py-4 gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10"
          onClick={onToggle}
          title="Show Version Compare"
        >
          <ChevronRight size={18} />
        </Button>
        <div className="flex items-center justify-center h-20 transform -rotate-90 origin-center whitespace-nowrap">
          <span className="text-xs text-muted-foreground">Compare</span>
        </div>
      </div>
    );
  }

  return (
    <aside className="w-[320px] flex-shrink-0 border-l bg-card h-full flex flex-col shadow-sm">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <Layers size={14} /> Version Compare
        </h3>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onToggle}
        >
          <ChevronRight size={14} />
        </Button>
      </div>

      <div className="px-4 py-3 border-b" data-testid="pattern-autosave-status-container">
        <div className={`rounded-md border px-3 py-2 text-xs font-medium ${autosaveToneClass}`} data-testid="pattern-autosave-status-badge">
          <span className="uppercase tracking-wide">Autosave</span>
          <p className="mt-1 normal-case" data-testid="pattern-autosave-status-message">{autosaveMessage}</p>
          {autosaveStatus === 'error' && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2 h-7 text-[11px]"
              onClick={onRetryAutosave}
              data-testid="pattern-autosave-retry-button"
            >
              Retry now
            </Button>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Pattern Piece Toggles */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Pattern Pieces</h4>
            
            <div className="flex items-center justify-between p-3 rounded-md border bg-muted/20">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-[#1e3a8a]"></div>
                <span className="text-sm font-medium">Front</span>
              </div>
              <Switch checked={showFront} onCheckedChange={setShowFront} data-testid="display-front-toggle-switch" />
            </div>

            <div className="flex items-center justify-between p-3 rounded-md border bg-muted/20">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-[#dc2626]"></div>
                <span className="text-sm font-medium">Back</span>
              </div>
              <Switch checked={showBack} onCheckedChange={setShowBack} data-testid="display-back-toggle-switch" />
            </div>

            {hasSidePiece && (
              <div className="flex items-center justify-between p-3 rounded-md border bg-muted/20" data-testid="display-side-toggle-row">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-[#c2410c]"></div>
                  <span className="text-sm font-medium">Side</span>
                </div>
                <Switch checked={showSide} onCheckedChange={setShowSide} data-testid="display-side-toggle-switch" />
              </div>
            )}

            {hasYokePiece && (
              <div className="flex items-center justify-between p-3 rounded-md border bg-muted/20" data-testid="display-yoke-toggle-row">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-[#0d9488]"></div>
                  <span className="text-sm font-medium">Yoke</span>
                </div>
                <Switch checked={showYoke} onCheckedChange={setShowYoke} data-testid="display-yoke-toggle-switch" />
              </div>
            )}

            {hasCollarPiece && (
              <div className="flex items-center justify-between p-3 rounded-md border bg-muted/20" data-testid="display-collar-toggle-row">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-[#0f766e]"></div>
                  <span className="text-sm font-medium">Collar</span>
                </div>
                <Switch checked={showCollar} onCheckedChange={setShowCollar} data-testid="display-collar-toggle-switch" />
              </div>
            )}

            {hasSleevePiece && (
              <div className="flex items-center justify-between p-3 rounded-md border bg-muted/20" data-testid="display-sleeve-toggle-row">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-[#9333ea]"></div>
                  <span className="text-sm font-medium">Sleeve</span>
                </div>
                <Switch checked={showSleeve} onCheckedChange={setShowSleeve} data-testid="display-sleeve-toggle-switch" />
              </div>
            )}
          </div>

          <Separator />

          {/* Measurement Toggle */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Display Options</h4>
            
            {resolvedDisplayOptions.measurements && (
              <div className="flex items-center justify-between p-3 rounded-md border bg-muted/20" data-testid="display-measurements-toggle-row">
                <div className="flex items-center gap-2">
                  {showMeasurements ? <Eye size={14} /> : <EyeOff size={14} />}
                  <span className="text-sm font-medium">Measurements</span>
                </div>
                <Switch checked={showMeasurements} onCheckedChange={setShowMeasurements} data-testid="display-measurements-toggle-switch" />
              </div>
            )}

            {resolvedDisplayOptions.seamWalk && (
              <div className="flex items-center justify-between p-3 rounded-md border bg-muted/20" data-testid="display-seam-walk-toggle-row">
                <div className="flex items-center gap-2">
                  {seamWalkMode ? <Eye size={14} /> : <EyeOff size={14} />}
                  <span className="text-sm font-medium">Shoulder Seam Walk</span>
                </div>
                <Switch checked={seamWalkMode} onCheckedChange={setSeamWalkMode} data-testid="display-seam-walk-toggle-switch" />
              </div>
            )}

            {resolvedDisplayOptions.sleeveOnBody && hasSleevePiece && (
              <div className="flex items-center justify-between p-3 rounded-md border bg-muted/20" data-testid="display-sleeve-on-body-toggle-row">
                <div className="flex items-center gap-2">
                  {sleeveOnBodyMode ? <Eye size={14} /> : <EyeOff size={14} />}
                  <span className="text-sm font-medium">Sleeve on Body</span>
                </div>
                <Switch checked={sleeveOnBodyMode} onCheckedChange={setSleeveOnBodyMode} data-testid="display-sleeve-on-body-toggle-switch" />
              </div>
            )}
          </div>

          <Separator />

          {/* Saved Versions */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Saved Versions ({versions.length})
              </h4>
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={fetchVersions}>
                Refresh
              </Button>
            </div>
            
            {loading && (
              <p className="text-xs text-muted-foreground text-center py-2">Loading...</p>
            )}
            
            {!loading && versions.length === 0 && (
              <div className="p-4 text-center text-xs text-muted-foreground border rounded-md bg-muted/10">
                No saved versions yet.<br/>
                Click &quot;Save Version&quot; to create one.
              </div>
            )}
            
            {!loading && versions.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Select up to 3 versions to compare
                </p>
                
                {versions.map((version) => {
                  const isSelected = selectedVersions.includes(version.id);
                  const colorIndex = selectedVersions.indexOf(version.id);
                  const color = colorIndex >= 0 ? VERSION_COLORS[colorIndex] : '#94a3b8';
                  
                  return (
                    <div 
                      key={version.id} 
                      className={`p-3 rounded-md border ${isSelected ? 'bg-blue-50/50 border-blue-200' : 'bg-muted/10'}`}
                    >
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleVersionToggle(version.id)}
                          disabled={!isSelected && selectedVersions.length >= 3}
                          className="mt-0.5"
                        />
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {isSelected && (
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: color }}
                              />
                            )}
                            <p className="text-sm font-semibold font-mono tabular-nums truncate">
                              {version.pattern_number}
                            </p>
                          </div>
                          
                          <p className="text-xs text-muted-foreground truncate">
                            {version.client_name} (#{version.client_number})
                          </p>
                          
                          <p className="text-xs text-muted-foreground">
                            {new Date(version.created_at).toLocaleDateString()}
                          </p>

                          <p
                            className="text-[11px] text-muted-foreground"
                            data-testid={`saved-version-geometry-version-${version.id}`}
                          >
                            Geometry: {version.geometry_version || version.formula_version || 'legacy'}
                          </p>
                          
                          {version.notes && (
                            <div className="mt-2 p-2 bg-muted/50 rounded text-xs">
                              {version.notes}
                            </div>
                          )}
                        </div>
                      </div>
                      
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </ScrollArea>
    </aside>
  );
}
